import { zodResolver } from '@hookform/resolvers/zod';
import DoneAllRounded from '@mui/icons-material/DoneAllRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import {
  Alert,
  Box,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PageHeader } from '../../components/states';
import { businessDate, money, qty as fmtQty } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { fromUnits, toUnits } from '../payments/allocation';
import { idempotency, invalidateMoney } from '../payments/kit/hooks';
import { links, todayIn } from '../payments/kit/labels';
import { BackLink, DetailSkeleton, ErrorState, SectionCard } from '../payments/kit/ui';
import { useGoodsReceipt } from './api';
import {
  baseToLineUnits,
  checkDraft,
  convertUnits,
  returnable,
  roundUnits,
  unitsToQty,
} from './calc';
import { UnitPick, type PickedUnit } from './UnitPick';
import {
  FormGrid,
  GhostButton,
  HeaderFields,
  LineInput,
  PickSourceCard,
  SummaryPanel,
} from './kit';
import { returnHeaderSchema, type ReturnHeaderValues } from './schemas';

/** Send received goods back to the supplier, line by line. */
export function PurchaseReturnFormPage() {
  const [params, setParams] = useSearchParams();
  const receiptId = params.get('receiptId');
  if (!receiptId)
    return (
      <>
        <BackLink to="/returns?tab=purchase" label="Returns & notes" />
        <PageHeader title="New purchase return" />
        <PickSourceCard
          kind="goodsReceipt"
          title="Which delivery are you sending back?"
          body="Goods leave stock at the current average cost. For anything already billed, a debit note credits the bill."
          onPick={(o) => setParams({ receiptId: o.id })}
        />
      </>
    );
  return <PurchaseReturnForm receiptId={receiptId} />;
}

function PurchaseReturnForm({ receiptId }: { receiptId: string }) {
  const { format } = useShop();
  const d = format.currencyDecimals;
  const { can } = useAuth();
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const receipt = useGoodsReceipt(receiptId);
  const [qtys, setQtys] = useState<Record<string, string>>({});
  // Lines returned in another unit than the receipt line's (1 box bought, 50 pc back).
  const [units, setUnits] = useState<Record<string, PickedUnit>>({});
  const [pending, setPending] = useState<ReturnHeaderValues | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ReturnHeaderValues>({
    resolver: zodResolver(returnHeaderSchema),
    defaultValues: { docDate: todayIn(format.timeZone), reason: '', notes: '' },
  });

  const rows = (receipt.data?.lines ?? []).map((l) => {
    const returnedBase = toUnits(l.qtyReturned) ?? 0n;
    const value = qtys[l.id] ?? '';
    const picked = units[l.id];
    const unit = picked ?? { unitId: l.unitId, symbol: l.unitSymbol, factor: l.factor };
    // The entered quantity in the receipt line unit.
    const q = picked
      ? convertUnits(toUnits(value) ?? 0n, picked.factor, l.factor)
      : (toUnits(value) ?? 0n);
    const left = returnable(l.qty, l.factor, returnedBase);
    // Receipt cost per base unit × factor × qty: a preview at the receipt cost.
    // Costs carry 6 decimals; the preview works in ten-thousandths.
    const cost = toUnits(l.unitCost.replace(/(\.\d{4})\d+$/, '$1')) ?? 0n;
    const perLineUnit = (cost * (toUnits(l.factor) ?? 10_000n)) / 10_000n;
    return {
      line: l,
      value,
      picked,
      unit,
      returned: baseToLineUnits(returnedBase, l.factor),
      billed: baseToLineUnits(toUnits(l.qtyBilled) ?? 0n, l.factor),
      max: picked ? convertUnits(left, l.factor, picked.factor) : left,
      value_: roundUnits((perLineUnit * q) / 10_000n, d),
    };
  });
  const check = checkDraft(
    rows.map((r) => ({ lineId: r.line.id, max: r.max, value: r.value })),
    {
      overMessage: 'More than is left',
    },
  );
  const total = rows.reduce((s, r) => s + (check.errors[r.line.id] ? 0n : r.value_), 0n);
  const anyBilled = rows.some((r) => r.billed > 0n && (toUnits(r.value) ?? 0n) > 0n);

  const save = useMutation({
    mutationFn: (v: ReturnHeaderValues) =>
      unwrap(
        api.POST('/purchase-returns', {
          params: idempotency(),
          body: {
            goodsReceiptId: receiptId,
            docDate: v.docDate,
            reason: v.reason || null,
            notes: v.notes || null,
            lines: rows
              .filter((r) => (toUnits(r.value) ?? 0n) > 0n)
              .map((r) => ({
                goodsReceiptLineId: r.line.id,
                qty: r.value.trim(),
                ...(r.picked ? { unitId: r.picked.unitId } : {}),
              })),
          },
        }),
      ),
    onSuccess: async (r) => {
      notify.success(
        r.debitNotes.length
          ? `${r.number} posted — ${r.debitNotes.map((n) => n.number).join(', ')} issued`
          : `${r.number} posted`,
      );
      await invalidateMoney(qc);
      await qc.invalidateQueries({ queryKey: ['stock'] });
      void navigate(links.purchaseReturn(r.id));
    },
    onError: (e) => {
      if (e instanceof ApiError)
        for (const det of e.details) {
          const field = det.path.split('.').pop();
          if (field === 'docDate' || field === 'reason' || field === 'notes')
            setError(field, { message: det.message });
        }
      notify.error(e);
    },
  });

  if (receipt.isLoading) return <DetailSkeleton />;
  if (receipt.isError || !receipt.data)
    return (
      <>
        <BackLink to="/returns?tab=purchase" label="Returns & notes" />
        <ErrorState error={receipt.error} onRetry={() => void receipt.refetch()} />
      </>
    );

  const gr = receipt.data;
  const posted = gr.status === 'POSTED';
  const allowed = can('purchases.return');

  return (
    <>
      <BackLink to="/returns?tab=purchase" label="Returns & notes" />
      <PageHeader
        title="New purchase return"
        subtitle={
          <>
            From{' '}
            <Link component={RouterLink} to={links.goodsReceipt(gr.id)} sx={{ fontWeight: 650 }}>
              {gr.number}
            </Link>
            {gr.supplierRef ? ` (${gr.supplierRef})` : ''} · {gr.contact.name} ·{' '}
            {businessDate(gr.docDate, format)} · {gr.location.name}
          </>
        }
        actions={
          <Button
            variant="outlined"
            onClick={() => void navigate('/returns/purchase/new', { replace: true })}
          >
            Change receipt
          </Button>
        }
      />
      {!posted && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {gr.number} was reversed — nothing can be returned from it.
        </Alert>
      )}
      {!allowed && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          You don’t have permission to record purchase returns.
        </Alert>
      )}
      <form
        noValidate
        onSubmit={(e) =>
          void handleSubmit((v) => {
            if (check.valid) setPending(v);
          })(e)
        }
      >
        <FormGrid
          main={
            <>
              <SectionCard
                flush
                title="What goes back"
                subtitle={`Leaves ${gr.location.name}; reserved stock can’t be returned`}
                action={
                  <GhostButton
                    onClick={() =>
                      setQtys(
                        Object.fromEntries(
                          rows.map((r) => [r.line.id, r.max > 0n ? unitsToQty(r.max) : '']),
                        ),
                      )
                    }
                  >
                    <DoneAllRounded fontSize="small" sx={{ mr: 0.75 }} />
                    Return everything
                  </GhostButton>
                }
              >
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          Received
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          Billed
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          Can return
                        </TableCell>
                        <TableCell align="right">Return</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow
                          key={r.line.id}
                          sx={{ opacity: r.max === 0n ? 0.55 : 1, verticalAlign: 'top' }}
                        >
                          <TableCell sx={{ pt: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {r.line.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {r.line.sku}
                              {r.returned > 0n &&
                                ` · ${fmtQty(fromUnits(r.returned, 0), format)} returned before`}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            className="tabular"
                            sx={{ display: { xs: 'none', md: 'table-cell' }, pt: 1.5 }}
                          >
                            {fmtQty(r.line.qty, format)} {r.line.unitSymbol}
                          </TableCell>
                          <TableCell
                            align="right"
                            className="tabular"
                            sx={{ display: { xs: 'none', md: 'table-cell' }, pt: 1.5 }}
                          >
                            {fmtQty(fromUnits(r.billed, 0), format)}
                          </TableCell>
                          <TableCell
                            align="right"
                            className="tabular"
                            sx={{
                              display: { xs: 'none', sm: 'table-cell' },
                              pt: 1.5,
                              fontWeight: 600,
                            }}
                          >
                            {fmtQty(fromUnits(r.max, 0), format)}
                            {r.picked ? ` ${r.unit.symbol}` : ''}
                          </TableCell>
                          <TableCell align="right">
                            <LineInput
                              value={r.value}
                              onChange={(v) => setQtys((p) => ({ ...p, [r.line.id]: v }))}
                              error={check.errors[r.line.id]}
                              label={`Quantity of ${r.line.description} to return`}
                              suffix={r.unit.symbol}
                              width={120}
                            />
                            <UnitPick
                              line={r.line}
                              value={r.unit}
                              onChange={(unit) => {
                                setQtys((p) => ({ ...p, [r.line.id]: '' }));
                                setUnits((p) => {
                                  const next: Record<string, PickedUnit> = {};
                                  for (const [id, u] of Object.entries(p))
                                    if (id !== r.line.id) next[id] = u;
                                  if (unit.unitId !== r.line.unitId) next[r.line.id] = unit;
                                  return next;
                                });
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
              <SectionCard title="Details">
                <HeaderFields
                  register={register}
                  errors={errors}
                  reasonPlaceholder="e.g. damaged in transit, wrong item"
                />
              </SectionCard>
            </>
          }
          side={
            <SummaryPanel
              title="Summary"
              rows={[
                { label: 'Receipt value', value: money(gr.totalCost, format) },
                { label: 'Lines returned', value: String(check.count) },
              ]}
              total={{ label: 'At receipt cost', value: money(fromUnits(total, d), format) }}
              explain={
                <>
                  Stock leaves at the current average cost, so the posted value can differ a little
                  from the receipt cost shown.{' '}
                  {anyBilled
                    ? 'Billed quantities get a debit note that credits the bill (settling it first).'
                    : 'Unbilled quantities simply won’t be billed any more.'}
                </>
              }
              actions={
                <>
                  <Button variant="outlined" onClick={() => void navigate('/returns?tab=purchase')}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<UndoRounded />}
                    disabled={!allowed || !posted || !check.valid}
                  >
                    Post return
                  </Button>
                </>
              }
            />
          }
        />
      </form>
      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title="Post this purchase return?"
        confirmLabel="Post return"
        body={
          <Box component="span">
            {check.count} line{check.count === 1 ? '' : 's'} back to {gr.contact.name}
            {anyBilled ? ', with a debit note on the bill' : ''}. Returns are final — they can’t be
            edited or cancelled.
          </Box>
        }
        onConfirm={() =>
          pending ? save.mutateAsync(pending).catch(() => undefined) : Promise.resolve()
        }
      />
    </>
  );
}
